import type { PortfolioListReportingSummaryResponseDTO } from "web_myplann/Infrastructure/Api/Api";
import {
	PORTFOLIO_SORT,
	type PortfolioSortKey,
} from "web_myplann/Portfolio/Components/Redesign/Filter/portfolioSortKey";
import { propertyValue } from "web_myplann/Portfolio/Components/Redesign/ListItem/propertyHelpers";
import type {
	CategoryItem,
	PortfolioCategory,
} from "web_myplann/Portfolio/Types/portfolioCategory";
import { PortfolioItemKind } from "web_myplann/Portfolio/Types/portfolioItemKind";

const getProducer = (item: CategoryItem): string =>
	item.kind === PortfolioItemKind.Portfolio ? (item.data.producer ?? "") : "";

const getName = (item: CategoryItem): string => item.data.name ?? "";

const getValue = (
	item: CategoryItem,
	summaryData: PortfolioListReportingSummaryResponseDTO[] | undefined,
): number | null => {
	if (item.kind === PortfolioItemKind.Property) {
		const raw = propertyValue(item.data);
		if (raw === null || raw.trim() === "") {
			return null;
		}
		const parsed = Number(raw);
		return Number.isFinite(parsed) ? parsed : null;
	}
	return (
		summaryData?.find((entry) => entry.id === item.data.id)?.summary
			?.totalValue ?? null
	);
};

const compareNumbersNullsLast =
	(get: (item: CategoryItem) => number | null, desc: boolean) =>
	(a: CategoryItem, b: CategoryItem): number => {
		const valueA = get(a);
		const valueB = get(b);
		if (valueA === null && valueB === null) {
			return 0;
		}
		if (valueA === null) {
			return 1;
		}
		if (valueB === null) {
			return -1;
		}
		return desc ? valueB - valueA : valueA - valueB;
	};

export const sortItems = (
	items: CategoryItem[],
	sort: PortfolioSortKey,
	desc: boolean,
	summaryData: PortfolioListReportingSummaryResponseDTO[] | undefined,
): CategoryItem[] => {
	if (sort === PORTFOLIO_SORT.producer) {
		const sorted = [...items].sort((a, b) =>
			getProducer(a).localeCompare(getProducer(b)),
		);
		return desc ? sorted.reverse() : sorted;
	}
	if (sort === PORTFOLIO_SORT.value) {
		return [...items].sort(
			compareNumbersNullsLast((item) => getValue(item, summaryData), desc),
		);
	}

	const sorted = [...items].sort((a, b) =>
		getName(a).localeCompare(getName(b)),
	);
	return desc ? sorted.reverse() : sorted;
};

export const flattenAndSortCategories = (
	categories: PortfolioCategory[],
	sort: PortfolioSortKey,
	desc: boolean,
	summaryData: PortfolioListReportingSummaryResponseDTO[] | undefined,
): CategoryItem[] =>
	sortItems(
		categories.flatMap((category) => category.items),
		sort,
		desc,
		summaryData,
	);
